<template>

  <v-container fluid class="pa-0">

    <div v-if="usingQr" class="app-sign-form-row justify-center">
      <v-btn v-if="keyIsReaded" color="warning" class="app-btn" @click="onResetBtnClicked">
        {{ $t('buttons.removesign') }}
      </v-btn>
      <v-btn v-else :disabled="componentDisabled" class="app-btn" color="primary" @click="readKeyClick">
        {{ $t('appSignFilePrivateKeyReader.read') }}
      </v-btn>
    </div>

    <v-form v-else ref="eSignReadKeyForm" autocomplete="off" @submit.prevent="submit">

      <div class="d-flex align-center">

        <app-text-field v-model="userId" :disabled="componentDisabled" label="appSignCloudProviderDefault.userId"
          required :clearable="!componentDisabled" class="app-sign-text-field">
        </app-text-field>

        <v-btn v-if="keyIsReaded" type="button" color="warning" class="ml-2 mt-1" @click="onResetBtnClicked">
          {{ $t('buttons.removesign') }}
        </v-btn>
        <v-btn v-else :disabled="componentDisabled" color="primary" class="ml-2 mt-1" @click="submit">
          {{ $t('appSignCloudProviderDefault.readKey') }}
        </v-btn>

      </div>

    </v-form>

    <div v-if="messageText" v-html="messageText" :class="[ 'info-panel pa-2', messageIsError ? 'error': 'success' ]">
    </div>

    <div v-if="warningText" v-html="warningText" class="info-panel pa-2 warning mt-1">
    </div>

  </v-container>

</template>

<script>
import AppTextField from '@/components/Common/AppTextField.vue';
import { getLocale } from '@/helpers';
import { logger } from '@/helpers/log.service';
import { appSettingsHelper } from '@/helpers/settings-helper';
import { actions as _act } from '@/models/store.actions';
import { IitCloudProviderSettings } from '@/models/settings.models';
import AppSignBasePrivateKeyReader from '@/sign/components/AppSignBasePrivateKeyReader';
import { executeCloudSignOperationWithConfirmationByQrCode } from '@/sign/helpers/cloud-sign-qr.helper';
import { AppSignKeyOperationType } from '@/sign/models/sign-constants';

export default {
  name: 'AppSignCloudProviderDefault',
  components: { AppTextField },
  extends: AppSignBasePrivateKeyReader,
  data() {
    return {
      userId: '',
      cloudProviderSettings: new IitCloudProviderSettings(null)
    };
  },
  computed: {
    usingQr() { return this.cloudProviderSettings.isSigningWithQr; }
  },
  created() {
    this.cloudProviderSettings = appSettingsHelper.iitCloudProviderSettings.find(p => p.id === this.signProvider);
    logger.debug(`[AppSignCloudProviderDefault:created] cloudProviderSettings`, this.cloudProviderSettings);
    this.prepareComponent();
  },
  methods: {
    prepareComponent() {
      let cloudProvider = this.getCloudProviderDataFromStore();
      if (cloudProvider) {
        if ('clientId' in cloudProvider) {
          this.userId = cloudProvider.clientId;
        } else if ('userId' in cloudProvider) {
          this.userId = cloudProvider.userId;
        }
      }
    },
    async readKey() {
      if (this.usingQr) {
        const ownerInfo = await executeCloudSignOperationWithConfirmationByQrCode(
          this.signProvider,
          this.euSignLibrary,
          'ReadPrivateKeyKSP',
          [null, this.cloudProviderSettings.ksp, true, 0],
          this.privateKeyType,
          AppSignKeyOperationType.Reading,
          this.cloudProviderSettings.mobileAppName
        );
        if (!ownerInfo) {
          throw new Error(getLocale('appSignCloudProviderDefault.keyNotFound').toString());
        }
        return ownerInfo;
      } else {
        const ownerInfo = await this.euSignLibrary.ReadPrivateKeyKSP(this.userId, this.cloudProviderSettings.ksp, true, 0);
        if (!ownerInfo) {
          throw new Error(getLocale('appSignCloudProviderDefault.keyNotFound').toString());
        }
        return ownerInfo;
      }
    },
    async tryToReadKey() {
      if (this.usingQr) {
        await this.readKeyClick({});
      } else {
        await this.submit();
      }
    },
    async submit() {
      if (this.$refs.eSignReadKeyForm.validate()) {
        this.saveCloudProviderDataToStore();

        let msg = this.isStamp ? 'appSignCloudProviderDefault.selectSealInMobileApp' : 'appSignCloudProviderDefault.selectKeyInMobileApp';

        const loadingText = getLocale(msg, { mobileAppName: this.cloudProviderSettings.mobileAppName }).toString();

        const ac = new AbortController();

        this.showLoading({ cancelCallback: () => { ac.abort(); } }, true, { text: loadingText });

        try {
          await this.readKeyClick({ signal: ac.signal });
        } finally {
          await this.hideLoading();
        }
      }
    },
    onPrepareComponent() {
      if (this.$refs.eSignReadKeyForm) {
        this.$refs.eSignReadKeyForm.resetValidation();
      }
    },
    getCloudProviderDataFromStore() {
      return this.isSeal
        ? this.$store.getters[_act.SIGN_GET_CLOUD_PROVIDER_SEAL](this.signProvider)
        : this.$store.getters[_act.SIGN_GET_CLOUD_PROVIDER](this.signProvider);
    },
    saveCloudProviderDataToStore() {
      let act = this.isSeal ? _act.SIGN_MUT_CLOUD_PROVIDER_SEAL : _act.SIGN_MUT_CLOUD_PROVIDER;
      this.$store.commit(act, {
        selectedCloud: this.signProvider,
        cloudProvider: { userId: this.userId }
      });
    }
  }
};
</script>
